import React from 'react';
import { Field, useFormikContext } from 'formik';

import { InputUI, DatePickerUI, AutoCompleteSelectUI, TimePickerUi, FormErrorUI, FormLabelUI } from '../Interface';
import { generateAutocompleteItems } from '../../utils/helper';
import useParam from '../../context/ParamContext';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';

const FormFields = ({
  viewField,
  selectedValues,
  autocompleteOptions,
  dialog,
  editButtonVisible,
  autocompleteData,
  roles,
  shouldSetNameFromClassification,
}) => {
  const { params } = useParam();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const handleGenerateStatusOption = () => {
    const filteredStatus = autocompleteData?.status?.statusItems?.filter((item) => item.onKanban);
    const generatedStatus = generateAutocompleteItems(filteredStatus, 'itemName', 'itemId');
    return generatedStatus;
  };

  return (
    <div className="grid grid-cols-4 gap-x-4 gap-y-2 md:mx-4">
      {!shouldSetNameFromClassification && (
        <div className="col-span-4 ">
          <Field
            type="text"
            name="name"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Rövid visszajelzés" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.name} />}
            disabled={viewField}
            InputLabelProps={{ shrink: values.name && values.name !== '' }}
          />
        </div>
      )}
      <div className={`${shouldSetNameFromClassification ? 'md:col-span-4' : 'md:col-span-1'} col-span-4`}>
        <Field
          type="autocomplete"
          name="classificationId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text={shouldSetNameFromClassification ? 'Rövid visszajelzés' : 'Besorolás'} />}
          variant="standard"
          helperText={<FormErrorUI message={errors.classificationId} />}
          onChange={(_e, newVal) => {
            if (shouldSetNameFromClassification) {
              setFieldValue('name', newVal?.label ?? '');
            }
            setFieldValue('classificationId', newVal?.value ?? null).then(() => {
              validateField('classificationId');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.classificationId !== '' }}
          selectedValue={values.classificationId}
          selectedLabelValue={selectedValues?.classification?.name}
          dataset={autocompleteOptions?.classification}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="date"
          name="startDate"
          component={DatePickerUI}
          value={values.startDate}
          fullWidth
          label={<FormLabelUI text="Kezdő dátum" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.startDate} />}
          onChange={(date) => {
            setFieldValue('startDate', date).then(() => {
              validateField('startDate');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.startDate !== '' }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="time"
          name="startDate"
          component={TimePickerUi}
          value={values.startDate}
          fullWidth
          label={<FormLabelUI text="Kezdő időpont" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.startDate} />}
          onChange={(date) => {
            setFieldValue('startDate', date).then(() => {
              validateField('startDate');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.startDate !== '' }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="date"
          name="endDate"
          component={DatePickerUI}
          value={values.endDate}
          fullWidth
          label={<FormLabelUI text="Befejező dátum" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.endDate} />}
          onChange={(date) => {
            setFieldValue('endDate', date).then(() => {
              validateField('endDate');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.endDate !== '' }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="text"
          name="duration"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Időintervallum" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.duration} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.duration !== '' }}
          onChange={(e) => {
            const newendDate = values.startDate.add(e.target.value, 'hour');
            setFieldValue('duration', e.target.value ?? '');
            setFieldValue('endDate', newendDate).then(() => {
              validateField('endDate');
            });
          }}
        />
      </div>
      {params.HIDE_TASKFEEDBACK_RELEVANT_DEP_FIELD !== 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="relevantDepartmentId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Érintett részleg" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.relevantDepartmentId} />}
            onChange={(_e, newVal) => {
              setFieldValue('relevantDepartmentId', newVal?.value ?? null).then(() => {
                validateField('relevantDepartmentId');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.relevantDepartmentId !== '' }}
            selectedValue={values.relevantDepartmentId}
            selectedLabelValue={selectedValues?.relevantDepartment?.name}
            dataset={autocompleteOptions?.relevantDepartment}
          />
        </div>
      )}
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="statusId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Státusz" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.statusId} />}
          onChange={(_e, newVal) => {
            setFieldValue('statusId', newVal?.value ?? null).then(() => {
              validateField('statusId');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.statusId !== '' }}
          selectedValue={values.statusId}
          selectedLabelValue={selectedValues?.status?.itemName}
          dataset={
            !editButtonVisible && roles.FFEDBACK_KANBAN_ONLY_KANBAN_STATUS_SELECT
              ? handleGenerateStatusOption()
              : autocompleteOptions?.status
          }
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="typeId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Típus" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.typeId} />}
          onChange={(_e, newVal) => {
            setFieldValue('typeId', newVal?.value ?? null).then(() => {
              validateField('typeId');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.typeId !== '' }}
          selectedValue={values.typeId}
          selectedLabelValue={selectedValues?.type?.itemName}
          dataset={autocompleteOptions?.type}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="priorityId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Prioritás" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.priorityId} />}
          onChange={(_e, newVal) => {
            setFieldValue('priorityId', newVal?.value ?? null).then(() => {
              validateField('priorityId');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.priorityId !== '' }}
          selectedValue={values.priorityId}
          selectedLabelValue={selectedValues?.priority?.itemName}
          dataset={autocompleteOptions?.priority}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="createdBy"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Tulajdonos" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.createdBy} />}
          onChange={(_e, newVal) => {
            setFieldValue('createdBy', newVal?.value ?? null).then(() => {
              validateField('createdBy');
            });
          }}
          disabled
          InputLabelProps={{ shrink: values.createdBy !== '' }}
          selectedValue={values.createdBy}
          selectedLabelValue={selectedValues?.user?.userName}
          dataset={autocompleteOptions?.user}
        />
      </div>
      {params.HIDE_TASKFEEDBACK_PROD_OPERATIONID_FIELD !== 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="text"
            name="prodOperationId"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Gyártási utasítás szám" />}
            variant="standard"
            disabled
            InputLabelProps={{
              shrink: selectedValues?.productionOperation?.label && selectedValues?.productionOperation?.label !== '',
            }}
            value={values?.prodOperationLabel ?? selectedValues?.productionOperation?.label ?? ''}
          />
        </div>
      )}
      {params.HIDE_TASKFEEDBACK_ITEMCODE_FIELD !== 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="text"
            name="itemCode"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Gyártott cikkszám" />}
            variant="standard"
            disabled
            InputLabelProps={{
              shrink: selectedValues?.itemCode && selectedValues?.itemCode !== '',
            }}
            value={values?.itemCode ?? selectedValues?.itemCode ?? ''}
          />
        </div>
      )}
      {params.HIDE_TASKFEEDBACK_OPERATION_FIELD !== 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="operationId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Művelet" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.operationId} />}
            onChange={(_e, newVal) => {
              setFieldValue('operationId', newVal?.value ?? null).then(() => {
                validateField('operationId');
              });
            }}
            disabled={!dialog?.data}
            InputLabelProps={{ shrink: values.operationId !== '' }}
            selectedValue={values.operationId}
            selectedLabelValue={selectedValues?.operation?.name}
            dataset={autocompleteOptions?.operations}
          />
        </div>
      )}
      {params.SHOW_TASKFEEDBACK_PROJECT_FIELD === 'Y' && (
        <>
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="projectId"
              component={AutoCompleteSelectUI}
              fullWidth
              label={<FormLabelUI text="Projekt" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.projectId} />}
              onChange={(_e, newVal) => {
                const findedProject = autocompleteData?.project.find((item) => item.projectId === newVal?.value);
                if (findedProject) {
                  setFieldValue('projectOrderNumber', findedProject.orderNumber || '');
                } else {
                  setFieldValue('projectOrderNumber', '');
                }
                setFieldValue('projectId', newVal?.value ?? null).then(() => {
                  validateField('projectId');
                });
              }}
              disabled={viewField}
              InputLabelProps={{ shrink: values.projectId !== '' }}
              selectedValue={values.projectId}
              selectedLabelValue={selectedValues?.project?.projectName}
              dataset={autocompleteOptions.project}
            />
          </div>
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="projectOrderNumber"
              component={InputUI}
              fullWidth
              label={<FormLabelUI text={getProjectOrderNumberName(params)} />}
              variant="standard"
              helperText={<FormErrorUI message={errors.projectOrderNumber} />}
              disabled={true}
              InputLabelProps={{ shrink: values.projectOrderNumber !== '' }}
              value={values.projectOrderNumber}
            />
          </div>
        </>
      )}
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="companyId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Vevő" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.companyId} />}
          onChange={(_e, newVal) => {
            setFieldValue('companyId', newVal?.value ?? null).then(() => {
              validateField('companyId');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.companyId !== '' }}
          selectedValue={values.companyId}
          selectedLabelValue={selectedValues?.company?.name}
          dataset={autocompleteOptions.company}
        />
      </div>
    </div>
  );
};

export default FormFields;
